import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ClaudeActionCreators } from '../../../redux/actions/creators';
import { AsyncActionStatus } from '../../../redux/enums';
import { ChaptersSelectors } from '../../../redux/selectors';
import { GenerationType } from '../../../../../shared/enums';
import DefaultModal from '../DefaultModal';
import CopyIcon from '../../../../assets/copy-icon.svg';
import GenerateIcon from '../../../../assets/generate-icon.svg';
import GoIcon from '../../../../assets/finger-icon.svg';

import styles from '../../../styles/views/components/AIChatbot/BotMessage.module.scss';

const BotMessage = ({
  message,
  question,
  isAnswerLoading,
  isTranscriptIncluded,
  onHide,
  generationType,
  tokensMultiplier,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [isOverwriteChaptersModalShown, setIsOverwriteChaptersModalShown] = useState(false);
  const chapters = useSelector(state => ChaptersSelectors.selectChaptersByProjectId(state, projectId));

  const toggleOverwriteChaptersModal = useCallback(() => (
    setIsOverwriteChaptersModalShown(prev => !prev)
  ), [setIsOverwriteChaptersModalShown]);

  const handeCopyToBuffer = useCallback(() => {
    navigator.clipboard.writeText(message);
  }, [message]);

  const handleRegenerateAnswer = useCallback(() => {
    dispatch(
      ClaudeActionCreators.askClaude[AsyncActionStatus.PENDING](
        question,
        projectId,
        isTranscriptIncluded,
        tokensMultiplier,
      ),
    );
  }, [dispatch, question, projectId, isTranscriptIncluded, tokensMultiplier]);

  const saveResultToOutline = useCallback(() => {
    dispatch(
      ClaudeActionCreators.wrapWithTagsAndSave[AsyncActionStatus.PENDING](
        message,
        projectId,
        onHide,
      ),
    );
  }, [dispatch, message, projectId, onHide]);

  const handleGO = useCallback(() => {
    if (chapters.length) {
      toggleOverwriteChaptersModal();
      return;
    }
    saveResultToOutline();
  }, [saveResultToOutline, toggleOverwriteChaptersModal, chapters]);

  return (
    <div className={styles.container}>
      <div className={styles.initials}>
        <span>{t('components.ai', { ns: i18next.Namespace.COMMON })}</span>
      </div>
      <div className={styles.messageWrapper}>
        <p>{message}</p>
        <div className={styles.buttonsWrapper}>
          <div className={styles.button} onClick={handeCopyToBuffer}>
            <CopyIcon />
            <span>{t('components.copy', { ns: i18next.Namespace.COMMON })}</span>
          </div>
          <div className={[styles.button, isAnswerLoading && styles.isDisable].join(' ')} onClick={handleRegenerateAnswer}>
            <GenerateIcon />
            <span>{t('components.regenerate', { ns: i18next.Namespace.COMMON })}</span>
          </div>
          {generationType === GenerationType.OUTLINE && (
            <div className={[styles.button, styles.border, isAnswerLoading && styles.isDisable].join(' ')} onClick={handleGO}>
              <GoIcon />
              <span>{`${t('components.go', { ns: i18next.Namespace.COMMON })}!`}</span>
            </div>
          )}
        </div>
      </div>
      <DefaultModal
        isShown={isOverwriteChaptersModalShown}
        title={t('overwriteChaptersModal.title', { ns: i18next.Namespace.COMMON })}
        message={t('overwriteChaptersModal.msg', { ns: i18next.Namespace.COMMON })}
        primaryAction={saveResultToOutline}
        secondaryAction={toggleOverwriteChaptersModal}
        closeModal={toggleOverwriteChaptersModal}
        primaryBtnText={t('button.overwrite', { ns: i18next.Namespace.COMMON })}
        secondaryBtnText={t('button.cancel', { ns: i18next.Namespace.COMMON })}
      />
    </div>
  );
};

BotMessage.propTypes = {
  message: PropTypes.string,
  generationType: PropTypes.string,
  question: PropTypes.string,
  isAnswerLoading: PropTypes.bool,
  isTranscriptIncluded: PropTypes.bool,
  onHide: PropTypes.func,
  tokensMultiplier: PropTypes.number,
};

BotMessage.defaultProps = {
  message: '',
  generationType: '',
  question: '',
  isAnswerLoading: false,
  isTranscriptIncluded: true,
  onHide: () => {},
  tokensMultiplier: 1,
};

export default BotMessage;
